import {
  HttpBadRequestError,
  HttpFailedToFetchException,
} from "../model/httpException";
import { ProjectSettings } from "../model/projectSettings";
import { getAuthHeaders } from "../utils/authHeaders";
import { logException } from "../utils/logUtils";

export class ProjectApi {
  private static readonly BASE_URL = import.meta.env.VITE_API_URL;
  private static readonly BASE_ENDPOINT = `project`;

  static async loadProjectSettings(
    projectId: string
  ): Promise<ProjectSettings> {
    // surprise surprise, it *can* be null here
    if (projectId === undefined || projectId === null) {
      return emptyProjectSettings();
    }

    const headers = await getAuthHeaders({});

    const response = await fetch(
      `${this.BASE_URL}/${this.BASE_ENDPOINT}/${projectId}`,
      {
        method: "GET",
        headers,
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      logException(`Project API Error (project/${projectId}):`, {
        status: response.status,
        statusText: response.statusText,
        body: errorText,
      });
      throw new HttpFailedToFetchException();
    }

    const data = await response.json();
    return data;
  }

  static async loadProjectIcon(projectId: string): Promise<Blob> {
    const headers = await getAuthHeaders();

    const response = await fetch(
      `${this.BASE_URL}/${this.BASE_ENDPOINT}/${projectId}/icon`,
      {
        method: "GET",
        headers,
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      logException(`Project API Error (project/${projectId}/icon):`, {
        status: response.status,
        statusText: response.statusText,
        body: errorText,
      });
      throw new HttpFailedToFetchException();
    }

    const blob = await response.blob();
    return blob;
  }

  static async updateProjectSettings({
    projectId,
    appName,
    packageName,
    appVersion,
  }: {
    projectId: string;
    appName: string;
    packageName: string;
    appVersion: string;
  }) {
    const params = {
      projectName: appName,
      appName,
      packageName,
      bundleId: packageName,
      appVersion,
    };

    const headers = await getAuthHeaders({
      "Content-Type": "application/json",
    });

    const response = await fetch(
      `${this.BASE_URL}/${this.BASE_ENDPOINT}/${projectId}/settings`,
      {
        method: "POST",
        headers,
        body: JSON.stringify(params),
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      logException(`Project API Error (project/${projectId}/settings):`, {
        status: response.status,
        statusText: response.statusText,
        body: errorText,
      });
      if (response.status === 400) {
        throw new HttpBadRequestError(errorText);
      } else {
        throw new HttpFailedToFetchException();
      }
    }
  }

  static async uploadIcon({
    projectId,
    icon,
  }: {
    projectId: string;
    icon: File;
  }) {
    // Create FormData for multipart upload
    const formData = new FormData();
    formData.append("icon", icon);

    const headers = await getAuthHeaders();

    const response = await fetch(
      `${this.BASE_URL}/${this.BASE_ENDPOINT}/${projectId}/icon`,
      {
        method: "POST",
        headers,
        body: formData,
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      logException(`Project API Error (project/${projectId}/icon):`, {
        status: response.status,
        statusText: response.statusText,
        body: errorText,
      });
      throw new HttpFailedToFetchException();
    }
  }
}
function emptyProjectSettings(): ProjectSettings {
  return {
    app_name: "",
    package_name: "",
    app_version: "",
  };
}
